import { Button, Card, Space, Select, Table } from "@arco-design/web-react";
import { Currency } from "@/components/Numeric";
import { BillAPI, VendorAPI } from "@/apis/purchasing";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BillAdd } from "./bill-add";
import { BillInfo } from "./bill-info";
import { BillEdit } from "./bill-edit";
import { FileUtil } from "@/utils";
import { BillStatusDef } from "@/enums/purchasing";
import { EnumLabel } from "@/components/Enum";
import _ from "lodash";

const BILL_STATUS = {
    Current: 1,
    'Current Partial': 2,
    Paid: 3,
    'Past Due': 4,
    'Past Due Partial': 5,
    Voided: 6
};

const BILL_STATUS_OPTIONS = [
    { label: 'ALL', value: 'ALL' },
    { label: 'Open', value: 'Open' },
    ...Object.entries(BILL_STATUS).map(([label, value]) => ({
        label,
        value: label
    }))
];

export const Bill = () => {
    const [vendorOptions, setVendorOptions] = useState([]);
    const [page, setPage] = useState(1);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [vendor, setVendor] = useState();
    const [status, setStatus] = useState("Open");

    const [totalBalance, setTotalBalance] = useState(0);
    const [pastDueBalance, setPastDueBalance] = useState(0);


    const getStatusList = useCallback((status) => {
        if (status === 'ALL') {
            return Object.values(BILL_STATUS);
        }
        if (status === 'Open') {
            return [
                BILL_STATUS['Current'],
                BILL_STATUS['Current Partial'],
                BILL_STATUS['Past Due'],
                BILL_STATUS['Past Due Partial']
            ];
        }
        return BILL_STATUS[status];
    }, []);

    const getVendors = useCallback(async () => {
        const values = await VendorAPI.actives();
        const sorted = _.sortBy(values, 'vendorName');
        setVendorOptions(sorted.map((val) => ({
            label: val.vendorName,
            value: val.id
        })));
    }, []);

    const getBills = useCallback(async () => {
        const statusList = getStatusList(status);
        const values = await BillAPI.query({ page, vendor, status: statusList });
        setData(values.records);
        setTotal(values.total);
    }, [page, vendor, status, getStatusList]);

    const getBalances = useCallback(async () => {
        const statusList = getStatusList(status);
        const totalBalanceData = await BillAPI.getTotalBalance({ vendor, status: statusList });
        const pastDueBalanceData = await BillAPI.getPastDueBalance({ vendor, status: statusList });
        setTotalBalance(totalBalanceData);
        setPastDueBalance(pastDueBalanceData);
    }, [vendor, status, getStatusList]);

    useEffect(() => {
        getVendors();
        getBills();
        getBalances();
    }, [getVendors, getBills, getBalances]);

    return (
        <Card title="Bills" extra={
            <Space>
                Total Balance: <Currency value={totalBalance} />
                Past Due: <Currency value={pastDueBalance} />
                <Select options={vendorOptions} allowClear placeholder="Vendor" style={{ width: 150 }}
                    showSearch
                    filterOption={(inputValue, option) => {
                        return option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }}
                    value={vendor} onChange={(val) => setVendor(val)} />
                <Select
                    value={status}
                    options={BILL_STATUS_OPTIONS}
                    placeholder="Status"
                    style={{ width: 120 }}
                    onChange={(val) => {
                        setStatus(val);
                    }}
                />
                <Link to={"/workspace/purchasing/bills/blank"}>
                    <Button type="primary">Add New Bill</Button>
                </Link>
                <Button type="primary" onClick={async () => {
                    const statusList = getStatusList(status);
                    const response = await BillAPI.exportExcel({ vendor, status: statusList });
                    FileUtil.download(response.data, "bills.xlsx");
                }}>Download</Button>
            </Space>
        }>
            <Table rowKey="id" columns={[
                {
                    title: "Bill#", render: (col, record, index) => {
                        return (
                            <Link to={"/workspace/purchasing/bills/" + record.id}>{record.billNo}</Link>
                        )
                    }
                },
                { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={BillStatusDef} value={record.status} /> },
                { title: "Bill Date", dataIndex: "billDate" },
                { title: "Due Date", dataIndex: "dueDate" },
                { title: "Vendor", dataIndex: "vendorName" },
                { title: "Total Amount", render: (col, record) => <Currency value={record.totalAmount} /> },
                { title: "Balance", render: (col, record) => <Currency value={record.totalAmount - record.paidAmount} /> },
                { title: "Invoice#", dataIndex: "invoiceNo" },
                { title: "Paid Date", dataIndex: "processDate" },
                { title: "Clear Date", dataIndex: "clearDate" },
            ]} data={data} pagination={{ total, current: page, pageSize: 20 }} onChange={(pagination) => setPage(pagination.current)} />
        </Card>
    );

};

Bill.Add = BillAdd;
Bill.Info = BillInfo;
Bill.Edit = BillEdit;